/**
 * Builds a `ScoringContext` from a concluded hand's live `MahjongState` and
 * scores it via the ruleset — the piece `advanceMatch.ts` needs before it can
 * apply point payments. Split out of `advanceMatch.ts` to keep both files
 * under the 200-line cap.
 */

import type { HandResult, MahjongState } from '../game/stateCore.js';
import { seatWind } from '../game/seatWinds.js';
import type { Meld, Seat, Tile } from '../game/types.js';
import type { Ruleset } from '../game/ruleset/types.js';
import type { HandScore, ScoringContext } from '../scoring/types.js';
import type { MatchState } from './MatchState.js';

interface WinnerDetail {
  readonly winner: Seat;
  readonly concealedTiles: readonly Tile[];
  readonly winningTile: Tile;
  readonly melds: readonly Meld[];
  readonly selfDrawn: boolean;
}

/** Derives the winner's concealed tiles/melds from a tsumo/ron result — `null` for `exhausted`. */
function winnerDetailFor(state: MahjongState, result: HandResult): WinnerDetail | null {
  if (result.kind === 'tsumo') {
    const hand = state.hands[result.winner] ?? [];
    return {
      winner: result.winner,
      // The drawn tile is a pointer already inside hands[winner] (stateCore's documented invariant).
      concealedTiles: hand.filter((t) => t.id !== result.winningTile.id),
      winningTile: result.winningTile,
      melds: state.melds[result.winner] ?? [],
      selfDrawn: true,
    };
  }
  if (result.kind === 'ron') {
    return {
      winner: result.winner,
      // The Ron'd tile was never added to the winner's hand — it came from the discarder's pile.
      concealedTiles: state.hands[result.winner] ?? [],
      winningTile: result.winningTile,
      melds: state.melds[result.winner] ?? [],
      selfDrawn: false,
    };
  }
  return null;
}

/**
 * True iff the win happened before any real turn action: the dealer's
 * untouched opening deal (Heavenly Hand — Tsumo, no discards anywhere yet),
 * or a Ron on the dealer's very first discard before anyone else acted
 * (Earthly Hand). Known simplification, not airtight against every
 * theoretical edge case: Ron never removes the discard from its pile (only
 * Pung/Chi/Kong claims do — see `claimTransitions.ts`'s `declareRon`), so
 * the dealer's opening discard is still sitting in `discards[dealer]` at the
 * moment of a genuine Earthly Hand Ron — hence the seat-by-seat check below
 * rather than a flat "every pile is empty." A prior claim on that exact
 * discard before this exact Ron is a contradiction in practice (a discard
 * can only be claimed once), so this is a pragmatic proxy for rare-hand
 * detection, not full turn-counting infrastructure.
 */
function isFirstTurn(state: MahjongState, dealer: Seat, result: HandResult): boolean {
  if (result.kind === 'ron' && result.from === dealer) {
    return state.discards.every((pile, seat) => (seat === dealer ? pile.length === 1 : pile.length === 0));
  }
  return state.discards.every((pile) => pile.length === 0);
}

function buildScoringContext(
  state: MahjongState,
  matchState: MatchState,
  result: HandResult,
  detail: WinnerDetail,
): ScoringContext {
  return {
    concealedTiles: detail.concealedTiles,
    melds: detail.melds,
    winningTile: detail.winningTile,
    seatWind: seatWind(detail.winner, matchState.dealer),
    // Round's 'east'/'south' values line up with Honor's directly — no cast needed.
    roundWind: matchState.round,
    selfDrawn: detail.selfDrawn,
    isFirstTurn: isFirstTurn(state, matchState.dealer, result),
  };
}

/** Scores a concluded hand via `ruleset.scoreHand`; `null` for `exhausted` hands or non-scoring rulesets. */
export function scoreConcludedHand(
  matchState: MatchState,
  state: MahjongState,
  result: HandResult,
  ruleset: Ruleset,
): HandScore | null {
  if (result.kind === 'exhausted') return null;
  const detail = winnerDetailFor(state, result);
  if (detail === null) return null;
  return ruleset.scoreHand(buildScoringContext(state, matchState, result, detail));
}
